import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, EventEmitter, inject, input, Input, model, Output, untracked, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Activity, ActivityEntityName, ActivityKind, ActivitySendStatus, ActivityShareType, ActivityTagName, ActivityType, Attachment, DateUtility, DurationUtility, getFileThumbnailUrl, RoundingUnit, Shareable, ShareableEntity } from '@jms/model';
import { CurrentUserService } from '@jms/ui/ui.standalone.services/current.user.service';
import { SanitizerHtmlPipe } from '@jms/utils/util.pipes';
import { AttachmentsComponent } from '@workbuddy/ui/components/attachments';
import { BadgeComponent } from '@workbuddy/ui/components/badge';
import { ButtonComponent } from '@workbuddy/ui/components/button';
import { CheckboxComponent } from '@workbuddy/ui/fields/checkbox';
import { LuxonCalendarModule, TimezoneDatePipe } from '@workbuddy/ui/fields/luxon.calendar';
import { SharingProComponent } from '@workbuddy/ui/fields/sharing.pro';
import { Command } from '@workbuddy/ui/model';
import { SelectionService, WorkBuddyDialogService } from '@workbuddy/ui/services';

import { ActivityContext, ActivityDisplayMode, ActivityHost, OVERRIDE_ACTIVITY_SERVICE } from '../../model';
import { ActivityEmailContactsPipe, ActivityKindColorPipe, ActivityKindPipe, ActivitySmsContactsPipe } from '../../pipes';
import { UiActivityUtility } from '../../utilities';
import { ActivityPreviewComponent } from '../activity.preview';
import { DividerComponent } from '@workbuddy/ui/components/divider';
import { FormsModule } from '@angular/forms';
import Enumerable from 'linq';
import { saveAs } from 'file-saver';
import { ActivityService } from '../../services';
import { EntityCreatorService } from '@workbuddy/modules/creators';
import { DurationPipeModule } from '@jms/utils/util.pipes.standalone';
import { lastValueFrom } from 'rxjs';

// Tags that scope an activity to a feed, and the only ones a reply inherits. Integration bookkeeping
// tags (memo serials, integration ids) must never be copied — downstream sync reads them as "already sent".
const THREAD_SCOPE_TAGS = [ActivityTagName.JobId, ActivityTagName.WorkOrderId, ActivityTagName.CodeId];

@Component({
  selector: 'activity-item',
  templateUrl: './activity.item.component.html',
  styleUrls: ['./activity.item.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    //'[class.active]': '$activity()?._id === selectedId',
    '[class.activity-sidebar]': '$display() === ActivityDisplayMode.SideBar',
    '[class.activity-fullscreen]': '$display() === ActivityDisplayMode.Fullscreen',
    '[class.activity-user]': '$isSystem()',
    '[class.activity-contactor-linked]': '$contractorLinked()',
    '[class.activity-customer-linked]': '$customerLinked()',
    '[class.activity-nested]': '$nested()',
  },
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AttachmentsComponent,
    LuxonCalendarModule,
    SanitizerHtmlPipe,
    ActivityKindPipe,
    ActivityKindColorPipe,
    ActivityEmailContactsPipe,
    ActivitySmsContactsPipe,
    CheckboxComponent,
    BadgeComponent,
    ButtonComponent,
    SharingProComponent,
    DividerComponent,
    DurationPipeModule,
    FormsModule,
  ]
})
export class ActivityItemComponent {

  public $context = input<ActivityContext>(null, { alias: 'context' });
  public $activity = model<Activity>(null, { alias: 'activity' });

  // Thread replies to render nested inside this root's card (already collapse-filtered by the list), and the count hidden behind the "All activities" expander.
  public $threadChildren = input<Activity[]>([], { alias: 'threadChildren' });
  public $hiddenChildCount = input<number>(0, { alias: 'hiddenChildCount' });
  // True for resolved threads with >1 reply — renders the expand/collapse toggle at the bottom of the card.
  public $threadCollapsible = input<boolean>(false, { alias: 'threadCollapsible' });
  // When true, this item is rendered as a compact reply sub-row inside a thread root's card (no timeline, card chrome or sharing control).
  public $nested = input<boolean>(false, { alias: 'nested' });

  @Input() public isFirst = false;
  @Input() public isLast = false;

  @Output() public edit = new EventEmitter<Activity>();
  @Output() public delete = new EventEmitter<Activity>();
  @Output() public share = new EventEmitter<ShareableEntity>();
  @Output() public threadResolvedChange = new EventEmitter<Activity>();
  @Output() public toggleThread = new EventEmitter<string>();

  private _activities = inject(ActivityService);
  private overrideActivities = inject(OVERRIDE_ACTIVITY_SERVICE, { optional: true }) as ActivityService | null;
  private creatorService = inject(EntityCreatorService);

  private get activityService(): ActivityService {

    return this.overrideActivities ?? this._activities;
  }

  protected $sendStatusVisbile = computed(() => {
    const activity = this.$activity();
    const isMessage = [ActivityKind.Email, ActivityKind.SMS].includes(activity?.kind);
    const isStatus = [ActivitySendStatus.Pending, ActivitySendStatus.Failed, ActivitySendStatus.Scheduled].includes(activity?.sendStatus);
    return isMessage && isStatus;
  })

  protected $sendStatusDisplay = computed(() => {
    const activity = this.$activity();
    const context = this.$context();

    switch (activity?.sendStatus) {
      case ActivitySendStatus.Sent:
        return 'Sent';

      case ActivitySendStatus.Failed:
        return 'Failed to send';

      case ActivitySendStatus.Pending:
        return 'Pending';

      case ActivitySendStatus.Scheduled:
        return `Scheduled for: ${DateUtility.getDateTime(activity?.scheduledSendTime, context?.timezone ?? activity?.timezone).toFormat('dd/MM/yy HH:mm')}`;

      default:
        return '';
    }
  });

  // Without the provider's reason (e.g. the attachment size limit) a Failed badge gives support nothing to act on
  protected $sendStatusTooltip = computed(() => {
    const activity = this.$activity();

    return activity?.sendStatus === ActivitySendStatus.Failed
      ? activity?.sendError ?? 'The message could not be sent'
      : null;
  });

  protected $sendStatusIcon = computed(() => {
    const activity = this.$activity();

    switch (activity?.sendStatus) {
      case ActivitySendStatus.Sent:
        return 'fa-regular fa-check';

      case ActivitySendStatus.Failed:
        return 'fa-regular fa-circle-exclamation';

      case ActivitySendStatus.Pending:
        return 'fa-regular fa-hourglass-clock';

      case ActivitySendStatus.Scheduled:
        return `fa-regular fa-clock`;

      default:
        return '';
    }
  })

  protected getPlainText(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  constructor() {

    effect(() => {

      const updateActivity = this.activityService.$updatedActivity();

      untracked(() => {

        const activity = this.$activity();

        if (activity?._id === updateActivity?._id) {
          this.$activity.set(updateActivity);
        }

      });


    });

  }

  protected $isThreadRoot = computed(() => {

    const activity = this.$activity();
    return !!activity?.threadId && activity.threadId === activity._id;
  });

  protected $isThreadChild = computed(() => {

    const activity = this.$activity();
    return !!activity?.threadId && activity.threadId !== activity._id;
  });

  protected $isThreadResolved = computed(() => {

    const activity = this.$activity();
    return this.$isThreadRoot() && !!activity?.threadResolved;
  });

  protected threadReplyText = '';
  protected threadReplying = false;

  /** A reply belongs to the thread's own entity and scope, not to whichever screen it was typed on. */
  private buildReplyBase(root: Activity): Partial<Activity> {

    const context = this.$context();
    const scopeTags = (root?.tags ?? []).filter(tag => THREAD_SCOPE_TAGS.includes(tag.name));

    // Roots raised before scope tags were carried fall back to the host screen.
    const tags = scopeTags.length
      ? scopeTags
      : context?.entity?.type === ActivityEntityName.Job
        ? [{ name: ActivityTagName.JobId, value: context.entity._id }]
        : [];

    const isThreadKind = root?.kind === ActivityKind.Thread;

    return {
      threadId: root?._id,
      system: false,
      active: true,
      tags,
      entityId: root?.entityId ?? context?.entity?._id,
      entity: root?.entity ?? context?.entity?.type,
      kind: isThreadKind ? ActivityKind.Thread : ActivityKind.Note,
      type: isThreadKind ? ActivityType.Thread : ActivityType.EntityComment,
    };
  }

  protected async sendThreadReply(): Promise<void> {

    const text = this.threadReplyText?.trim();
    if (!text) return;

    const activity = this.$activity();
    if (activity?.threadResolved) return;

    const reply = {
      _id: undefined,
      ...this.buildReplyBase(activity),
      note: text,
    } as Activity;

    this.threadReplying = true;
    try {
      await lastValueFrom(this.activityService.save(reply));
      this.threadReplyText = '';
    } finally {
      this.threadReplying = false;
    }
  }

  protected openFullReplyDialog(): void {

    const activity = this.$activity();
    if (activity?.threadResolved) return;

    const context = this.$context();
    const reply = {
      _id: undefined,
      ...this.buildReplyBase(activity),
      note: this.threadReplyText?.trim() || null,
      attachments: [],
      systemSharing: true,
      shared: [],
      sensitive: false,
    } as Activity;

    this.creatorService.show({
      entity: ActivityEntityName.Activity,
      config: { data: { activity: reply, context: { ...context }, submit: replyActivity => this.activityService.save(replyActivity) } },
    });
  }

  protected async toggleThreadResolved(): Promise<void> {

    const activity = this.$activity();
    if (!activity?._id) return;

    const updated = { ...activity, threadResolved: !activity.threadResolved } as Activity;
    await lastValueFrom(this.activityService.save(updated));
    this.threadResolvedChange.emit(updated);
  }

  protected onToggleThread(): void {

    const activity = this.$activity();
    if (activity?._id) this.toggleThread.emit(activity._id);
  }

  protected $isClaims = computed(() => {
    const context = this.$context();
    return [
      ActivityHost.LocalClaims,
      ActivityHost.RemoteClaims
    ].includes(context?.host);
  });

  protected $isSystem = computed(() => {
    const activity = this.$activity();
    return activity?.system ?? false;
  });

  /** The thread reply box adds an activity, so a host that grants adding apart from editing hides it without that grant. */
  protected $canReply = computed(() => this.$context()?.right?.createActivity ?? true);

  protected $isClientPortal = computed(() => {
    const context = this.$context();
    return context?.host === ActivityHost.ClientPortal;
  });

  protected $display = computed(() => {
    const context = this.$context();
    return context?.display ?? ActivityDisplayMode.SideBar;
  });

  protected $workOrderEntityName = computed(() => {
    const context = this.$context();
    const activity = this.$activity();
    const validName = UiActivityUtility.getEntityName(context?.job, activity);
    return validName;
  });

  protected $jobEntityName = computed(() => {
    const context = this.$context();

    if (![ActivityEntityName.Site, ActivityEntityName.SitePro].includes(context?.entity?.type))
      return null;

    const activity = this.$activity();
    const validName = UiActivityUtility.getEntityName(context?.job, activity, context?.entity?.type);
    return validName;
  });

  protected $overrideTemplateColumns = computed(() => {
    const min = this.$display() === ActivityDisplayMode.SideBar ? '120px' : '250px';
    return `repeat(auto-fill, minmax(${min}, 1fr))`;
  });

  protected $getAttachmentsStyle = computed(() => {
    const sidebar = this.$display() === ActivityDisplayMode.SideBar;
    const count = this.$activity()?.attachments?.length ?? 0;
    if (!sidebar && count <= 1) {
      return { 'grid-template-columns': '1fr' };
    }
    return { 'grid-template-columns': 'repeat(auto-fill, minmax(120px, 1fr))' };
  });

  protected $contractorLinked = computed(() => {
    const activity = this.$activity();

    if (!activity) return false;
    return !!activity?.tags?.find(t => t.name === ActivityTagName.ContractorActivityId);

  });

  protected $customerLinked = computed(() => {
    const activity = this.$activity();

    if (!activity) return false;
    return !!activity?.tags?.find(t => t.name === ActivityTagName.CustomerActivityId);

  })

  protected $attachments = computed(() => {

    const activity = this.$activity();

    let attachments = activity?.attachments ?? [];

    if (activity?.kind === ActivityKind.Form) {

      const pdf = Enumerable
        .from(attachments)
        .where(x => x.type === 'File')
        .doAction(x => {
          // Always use cache burster in this scenario where type is File and kind is Form
          x.thumbnail = getFileThumbnailUrl(x, true);
        })
        .firstOrDefault();

      const form = Enumerable
        .from(attachments)
        .firstOrDefault(x => x.type === 'Form');

      attachments = pdf
        ? [pdf]
        : [form];

      attachments = attachments.filter(x => !!x);

    }

    // const attachments = activity.kind === ActivityKind.Form
    //   ? Enumerable
    //     .from(activity?.attachments ?? [])
    //     .where(x => {
    //       return activity.kind === ActivityKind.Form
    //         ? x.type !== 'File'
    //         : true;
    //     })
    //     .doAction(x => {
    //       if (activity.kind === ActivityKind.Form) {
    //         if (!x?.thumbnail) {
    //           // no form thumbnail
    //           console.log('no form thumbnail');
    //         }
    //       }
    //     })
    //     .toArray()
    //   : activity?.attachments ?? [];

    const claimedAttachments = activity
      ?.claimedAttachments
      ?.map(x => {
        return {
          ...x,
          syncing: true
        };
      }) ?? [];

    return [...attachments, ...claimedAttachments];

  });

  protected $canSelect = computed(() => {

    const context = this.$context();
    const activity = this.$activity();
    const isCustomerLinked = this.$customerLinked();

    const isLocalClaims = context?.host === ActivityHost.LocalClaims;
    const isSystem = activity.system;
    //const isSyncing = !!activity?.claimedAttachments?.length;

    if (isLocalClaims && isCustomerLinked) {
      return false;
    } else if (isSystem) {
      return false;
    } else {
      return true;
    }
  });

  protected $canEdit = computed(() => {

    const context = this.$context();
    const activity = this.$activity();
    const user = this?.currentUser?.user;

    return UiActivityUtility.canEdit(context, user, activity);

    //const isCustomerLinked = this.$customerLinked();

    // const isCurrentUser = activity?.createdBy?._id === this.currentUser.user?._id;
    // const isSystem = activity.system;

    // if (isSystem || isCustomerLinked)
    //   return false;
    // else if (isCurrentUser)
    //   return context?.right?.manageActivity;
    // else
    //   return context?.right?.manageOtherUserActivity;

  });

  protected $formStageAvailable = computed(() => {

    const activity = this.$activity();
    if (activity?.kind !== ActivityKind.Form) {
      return true;
    }

    const stageId = activity.entity === ActivityEntityName.WorkOrder
      ? activity.entityId
      : activity.tags?.find(t => t.name === ActivityTagName.WorkOrderId)?.value;

    if (!stageId) {
      return true;
    }

    // The stage view hands over its job with `workOrders: []`; an empty list means the stages are not known here, not that this one was deleted.
    const workOrders = this.$context()?.job?.workOrders;
    if (!workOrders?.length) {
      return true;
    }

    return workOrders.some(wo => wo?._id === stageId);
  });

  protected $canContinueForm = computed(() => {

    const activity = this.$activity();
    if (!activity?.isDraft || activity?.kind !== ActivityKind.Form) {
      return false;
    }

    if (this.$isClientPortal()) {
      return false;
    }

    return this.$formStageAvailable();
  });

  protected $canEditForm = computed(() => this.$canEdit() && this.$formStageAvailable());

  protected $canDelete = computed(() => {

    const context = this.$context();
    const activity = this.$activity();
    const user = this?.currentUser?.user;

    return UiActivityUtility.canDelete(context, user, activity);

  });

  protected $canShare = computed(() => {

    const context = this.$context();
    const activity = this.$activity();
    const user = this?.currentUser?.user;

    return UiActivityUtility.canShare(context, user, activity);

  });

  protected $supportsSharing = computed(() => {

    const context = this.$context();
    const activity = this.$activity();

    return UiActivityUtility.supportsSharing(context, activity);

  });


  protected $commands = computed(() => {

    const canEdit = this.$canEdit();
    const canEditForm = this.$canEditForm();
    const canDelete = this.$canDelete();
    const isThreadRoot = this.$isThreadRoot();
    const isResolved = this.$isThreadResolved();

    const activity = this.$activity();
    const isForm = activity?.kind === ActivityKind.Form;
    const canOpenEditor = isForm ? canEditForm : canEdit;

    return [
      new Command({
        label: 'Edit', iconLeft: 'fa-regular fa-edit', action: () => {
          if (!canOpenEditor) return;
          this.edit.emit(activity)
        },
        hidden: !canOpenEditor
      }),
      new Command({
        label: isResolved ? 'Reopen Thread' : 'Resolve Thread', iconLeft: isResolved ? 'fa-regular fa-arrow-rotate-left' : 'fa-regular fa-circle-check', action: () => {
          this.toggleThreadResolved();
        },
        hidden: !isThreadRoot || !canEdit
      }),
      new Command({
        label: 'Delete', iconLeft: 'fa-regular fa-trash', action: () => {
          if (!canDelete) return;
          this.delete.emit(activity);
        },
        hidden: !canDelete
      })
    ];

  });

  protected dialogService = inject(WorkBuddyDialogService);
  protected currentUser = inject(CurrentUserService);
  protected selectionService = inject(SelectionService);

  protected ActivityShareType = ActivityShareType;
  protected ActivityDisplayMode = ActivityDisplayMode;
  protected ActivityKind = ActivityKind;

  protected onPreview(attachment: Attachment) {

    const context = this.$context();

    this.dialogService
      .open(ActivityPreviewComponent, {
        showHeader: false,
        closeOnEscape: true,
        dismissableMask: true,
        style: {
          display: 'contents'
        },
        contentStyle: {
          display: 'contents',
        },
        data: {
          activity: this.$activity(),
          attachmentId: attachment?._id,
          tenantUrl: context?.tenantUrl
        }
      });
  }

  protected onEditForm() {
    if (!this.$canEditForm()) return;
    const activity = this.$activity();
    this.edit.emit(activity)
  }

  protected onShare(shareable: Shareable) {
    this.share.emit({ _id: this.$activity()._id, ...shareable });
  }

  protected async downloadFormPDF() {
    const activity = this.$activity();
    const attachment = activity?.attachments?.find(x => x.type === 'File');

    if (!attachment) return;

    const name = await lastValueFrom(this.activityService.getAttachmentFileName(activity._id, ActivityEntityName.Form));

    saveAs(attachment.path, name ?? attachment.title);
  }

  protected formatCallLogDetails() {
    const activity = this.$activity();

    const details = [];
    if (activity.callDate) {
      const dateTimeFormat = new TimezoneDatePipe()
      const dateValue = dateTimeFormat.transform(activity.callDate, this.$context().timezone, true);
      details.push(dateValue);
    }

    if (activity.callDirection) {
      details.push(activity.callDirection);
    }

    if (activity.callOutcome) {
      details.push(activity.callOutcome);
    }

    if (activity.callDuration) {
      const durationDisplay = DurationUtility.stringify(activity.callDuration, RoundingUnit.Second);
      details.push(durationDisplay);
    }

    return details.join(', ');
  }
}
